/*
 * Copyright 2022 Siemens
 */

import React from 'react';
import {Content} from '../common/content';
import {Communication} from '../services/communication';
import {
  SwwcCheckbox,
  SwwcDefaultCell,
  SwwcDefaultCellGroup,
  SwwcIcon,
  SwwcInputField,
  SwwcLabel,
} from '@com.siemens.plm.horizon/swwc/bindings/react';
import {CurrentWorkspace, Settings} from '../services/settings';
import {CommandBar} from '../common/commandbar';
import App from '../App';
import {SetOptional} from 'type-fest';
import {Panel} from '../common/panel';

interface WorkspaceItem {
  id: string;
  name: string;
  owner: string;
  restricted: boolean;
  userCount: number;
}

interface WorkspaceState {
  isLoading: boolean;
  currentWorkspace: CurrentWorkspace;
  allUsers: ReadonlyArray<WorkspaceItem>;
  restrictedUsers: ReadonlyArray<WorkspaceItem>;

  form?: SetOptional<WorkspaceItem, keyof WorkspaceItem> | undefined;
  formReadOnly: boolean;
  formSubmission: boolean;
  selected?: WorkspaceItem | undefined;
}

export class Workspace extends React.Component<{}, WorkspaceState> {
  readonly #symbol = Symbol('Workspace');

  constructor(props: {}, state: WorkspaceState) {
    super(props, state);
    this.state = {
      formReadOnly: true,
      formSubmission: false,

      isLoading: true,
      currentWorkspace: Settings.currentWorkspace.value,
      allUsers: [],
      restrictedUsers: [],
    };
  }

  override componentDidMount() {
    if (super.componentDidMount) super.componentDidMount();
    Settings.currentWorkspace.addObserver(
      this.#symbol,
      (currentWorkspace) => {
        this.setState({currentWorkspace});
      },
      true,
    );
    this.#reloadData(true);
  }

  #reloadData(skipLoadingUpdate?: true): void {
    if (skipLoadingUpdate === undefined) this.setState({isLoading: true});
    Communication.fetch<ReadonlyArray<WorkspaceItem>>('workspaces', 'GET', 'json+array').then(
      (value) => {
        if (value.status === 'ok' && value.data !== null) {
          const newState = {
            isLoading: false,
            allUsers: [] as Array<WorkspaceItem>,
            restrictedUsers: [] as Array<WorkspaceItem>,
          };
          for (const item of value.data)
            if (item.restricted) newState.restrictedUsers.push(item);
            else newState.allUsers.push(item);
          this.setState(newState);
        } else if (value.status === 'ok') App.toast('warn', 'Workspace', undefined, `Data of 'workspaces' is invalid`);
        else
          App.toast(
            'error',
            'Workspace',
            undefined,
            `Loading of 'workspaces' failed: ${
              ('error' in value && value.error.message) ||
              `[Code: ${value.code}] ${('data' in value && value.data) || 'Unknown'}`
            }`,
          );
      },
      (err) => {
        App.toast(
          'error',
          'Workspace',
          undefined,
          `Loading of 'workspaces' failed: ${(err instanceof Error && err.message) || err}`,
        );
      },
    );
  }

  override componentWillUnmount() {
    Settings.currentWorkspace.removeObserver(this.#symbol);
    if (super.componentWillUnmount) super.componentWillUnmount();
  }

  #workspaceElements(workspaces: ReadonlyArray<WorkspaceItem>): ReadonlyArray<React.ReactNode> {
    return workspaces.map((workspace) => (
      <SwwcDefaultCell
        key={workspace.id}
        cellTitle={workspace.name}
        subTitle={workspace.id}
        selected={this.state.selected === workspace}
        onClick={() => {
          this.setState({selected: workspace, form: workspace, formReadOnly: true, formSubmission: false});
          App.navigate(`/${App.pageName}/${workspace.id}`);
        }}
      >
        <div slot='right-column-area'>
          {this.state.currentWorkspace.id === workspace.id ||
          (this.state.currentWorkspace.id === undefined && this.state.currentWorkspace.name === workspace.name) ? (
            <SwwcIcon
              name='cmdFilledStar24'
              size='lg'
              title='Current workspace'
            />
          ) : (
            <SwwcIcon
              name='cmdFavorites16'
              size='lg'
              title='Make active workspace'
              onClick={(event) => {
                event.stopPropagation();
                Settings.selectWorkspace(workspace);
              }}
            />
          )}
          <SwwcIcon
            name='cmdTrash16'
            size='lg'
            title='Delete workspace'
            onClick={(event) => {
              event.stopPropagation();
              this.#panelHide(workspace);
            }}
          ></SwwcIcon>
        </div>
        <div slot='bottom-area'>
          <SwwcLabel label='Owner ID'>
            <span slot='content'>{workspace.owner ?? 'N/A'}</span>
          </SwwcLabel>
        </div>
      </SwwcDefaultCell>
    ));
  }

  #panelShow(item: WorkspaceItem, isEdit?: true): void {
    this.setState({
      selected: item,
      form: isEdit ? JSON.parse(JSON.stringify(item)) : item,
      formReadOnly: !isEdit,
      formSubmission: false,
    });
    App.navigate(`/${App.pageName}/${item.id}${isEdit ? '/edit' : ''}`);
  }

  #panelHide(deleteItem?: WorkspaceItem): void {
    this.setState({
      form: undefined,
      selected: undefined,
      formReadOnly: false,
      formSubmission: false,
    });
    App.navigate(`/${App.pageName}`);

    if (deleteItem) {
      if (Settings.currentWorkspace.value.id === deleteItem.id)
        App.toast('warn', 'Workspace', undefined, `Current workspace is not deletable`);
      else if (deleteItem.name === 'default') App.toast('warn', 'Workspace', undefined, '"default" workspace is not deletable');
      else
        App.modal(
          'error',
          'Workspace',
          `Are you sure you want to delete "${deleteItem.name}"?`,
          (action) => {
            if (action !== 'yes') return;

            Communication.fetch({workspace: deleteItem.id ?? deleteItem.name}, 'DELETE', 'json+object').then(
              (res) => {
                if (res.status === 'ok') {
                  App.toast('info', 'Workspace', undefined, `Workspace ${deleteItem.name} is deleted [ID: ${deleteItem.id}]`);
                  setTimeout(() => this.#reloadData(true), 100);
                } else {
                  App.toast(
                    'error',
                    'Workspace',
                    undefined,
                    `Failed to delete workspace ${deleteItem.name} [ID: ${deleteItem.id}]: ${
                      'data' in res ? (typeof res.data === 'string' ? res.data : JSON.stringify(res.data)) : res.error.message
                    }`,
                  );
                }
              },
              (val) => {
                App.toast(
                  'error',
                  'Workspace',
                  undefined,
                  `Unknown error during deletion of workspace ${deleteItem.name} [ID: ${deleteItem.id}]: ${val}`,
                );
              },
            );
          },
          'Delete',
          'Cancel',
        );
    }
  }

  override render() {
    return (
      <Content
        section='Workspace'
        isLoading={this.state.isLoading}
      >
        <CommandBar
          addCommand={() => {
            this.setState({form: {}, selected: undefined, formReadOnly: false, formSubmission: false});
            App.navigate(`/${App.pageName}`);
          }}
          reloadCommand={() => this.#reloadData()}
        ></CommandBar>
        <div className='paneled'>
          <div className='content'>
            <SwwcDefaultCellGroup
              groupTitle='Public workspace(s)'
              expanded={true}
              cellDensity='cozy'
            >
              {this.#workspaceElements(this.state.allUsers)}
            </SwwcDefaultCellGroup>
            <SwwcDefaultCellGroup
              groupTitle='Private workspace(s)'
              expanded={true}
              cellDensity='cozy'
            >
              {this.#workspaceElements(this.state.restrictedUsers)}
            </SwwcDefaultCellGroup>
          </div>
          {this.state.form && (
            <Panel
              title={this.state.form.id === undefined ? `New Workspace` : `Workspace: ${this.state.form.name}`}
              isEditable={this.state.form.name !== 'Personal'}
              isDeletable={this.state.selected?.id !== this.state.currentWorkspace.id}
              isReadOnly={this.state.formReadOnly}
              isSubmission={this.state.formSubmission}
              onClose={() => this.#panelHide()}
              onDelete={() => this.#panelHide(this.state.selected!)}
              // onEdit={() => this.#panelShow(this.state.selected!, true)}
              onSubmitCancel={() => this.#panelShow(this.state.selected!)}
              onSubmit={() => {
                const isCreation = this.state.form === undefined || this.state.form.id === undefined;
                this.setState({formReadOnly: true, formSubmission: true});
                Communication.fetch<WorkspaceItem, false>(
                  isCreation ? {isWorkspaces: true} : {workspace: this.state.form!.id!},
                  isCreation ? 'POST' : 'PATCH',
                  'json+object',
                  this.state.form,
                ).then(
                  (res) => {
                    if (res.status === 'ok') {
                      if (
                        this.state.form !== undefined &&
                        this.state.form !== this.state.selected &&
                        this.state.form.name === res.data.name
                      )
                        this.#panelShow(
                          this.state.allUsers.find((ele) => ele.name === res.data.name) ??
                            this.state.restrictedUsers.find((ele) => ele.name === res.data.name) ??
                            res.data,
                        );
                      else App.toast('info', 'Workspace', undefined, `Submission of workspace "${res.data.name}" is completed`);
                      setTimeout(() => this.#reloadData(true), 100);
                    } else {
                      App.toast(
                        'error',
                        'Workspace',
                        undefined,
                        'data' in res
                          ? typeof res.data === 'string'
                            ? res.data
                            : JSON.stringify(res.data)
                          : res.error.message,
                      );
                      if (this.state.form !== undefined && this.state.form !== this.state.selected) {
                        this.setState({formReadOnly: false, formSubmission: false});
                      }
                    }
                  },
                  (val) => {
                    App.toast(
                      'error',
                      'Workspace',
                      undefined,
                      `Unknown error during ${isCreation ? 'creation' : 'updating'} of workspace: ${val}`,
                    );
                    if (this.state.form !== undefined && this.state.form !== this.state.selected) {
                      this.setState({formReadOnly: false, formSubmission: false});
                    }
                  },
                );
              }}
            >
              <SwwcInputField
                name='id'
                label='Identifier'
                value={this.state.form.id ?? ''}
                readonly={true}
              />
              <SwwcInputField
                name='name'
                autofocus={true}
                label='Name'
                charLength={this.state.form.name?.length ?? 0}
                value={this.state.form.name ?? ''}
                onValueChanged={(val) => (this.state.form!.name = val.detail.newValue)}
                readonly={this.state.formReadOnly}
                errorMessage='Name is required'
                required={true}
                minlength={1}
                maxlength={Settings.configuration.limit.param}
              />
              <SwwcInputField
                name='ownerId'
                label='Owner Id'
                value={this.state.form.owner ?? 'N/A'}
                readonly={true}
              />
              <SwwcInputField
                name='owner'
                label='Owner'
                value='N/A'
                readonly={true}
              />
              <SwwcLabel label='Permissions' />
              <SwwcCheckbox
                label='Restricted'
                disabled={this.state.formReadOnly}
                checked={this.state.form.restricted ?? false}
                onCheckChanged={(val) => (this.state.form!.restricted = val.detail.checked)}
              />
            </Panel>
          )}
        </div>
      </Content>
    );
  }
}
